import { useState, useMemo } from 'react';
import { Button, Card, Flex, Select, Stack, useTheme, spacing } from '@fti/design-system';
import { useExecutionReflections, useExportReflections } from '../../../api/media-sim/executions';
import { ExportDropdown } from '../shared/ExportDropdown';
import { ReflectionCategoryTabs } from '../reflections/ReflectionCategoryTabs';
import { ReflectionFieldCard } from '../reflections/ReflectionFieldCard';
import type {
  StaticReflectionNotes,
  TopicReflectionNotes,
  SubjectReflectionNotes,
  ReflectionItem,
} from '../../../types/media-sim';

interface ExecutionReflectionsViewProps {
  projectId: string;
  runId: string;
  executionId: string;
}

const TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'static', label: 'Static' },
  { value: 'dynamic_topic', label: 'Topic' },
  { value: 'dynamic_subject', label: 'Subject' },
];

const TOPIC_FIELDS = [
  { key: 'event_scale', label: '(A) Event Scale', description: 'Is topic(s) treated as a major event or a minor event?', category: 'newsworthiness' },
  { key: 'topic_justification_language', label: '(B) Topic Justification Language', description: 'What language is used to justify why the publication covered topic?', category: 'newsworthiness' },
  { key: 'topic_dominant_lens', label: '(A) Topic Dominant Lens', description: 'What interpretive lens or story angle does outlet apply towards topic?', category: 'editorial_angle_selection' },
  { key: 'topic_quote_authority', label: '(A) Topic Quote Authority', description: 'What types of individuals or organizations get quoted in stories about topic?', category: 'quotation_strategy' },
  { key: 'topic_events', label: '(A) Topic Events', description: 'What similar events to topic has this outlet covered or discussed?', category: 'coverage_memory' },
];

const SUBJECT_FIELDS = [
  { key: 'subject_focus', label: '(A) Subject Focus', description: 'Does this outlet publish articles primarily about subject or are they only mentioned briefly?', category: 'newsworthiness' },
  { key: 'subject_justification_language', label: '(B) Subject Justification Language', description: 'What language is used to justify why the publication covered subject?', category: 'newsworthiness' },
  { key: 'subject_dominant_lens', label: '(A) Subject Dominant Lens', description: 'What interpretive lens or story angle does outlet apply towards subject?', category: 'editorial_angle_selection' },
  { key: 'subject_quotes', label: '(A) Subject Quotes', description: 'Is subject or individuals associated with subject frequently quoted by outlet?', category: 'quotation_strategy' },
  { key: 'subject_critics', label: '(B) Subject Critics', description: 'Are critics of subject frequently quoted? What critics or types of voices usually supply criticism?', category: 'quotation_strategy' },
  { key: 'subject_events', label: '(A) Subject Events', description: 'What events, if any, has outlet covered about subject?', category: 'coverage_memory' },
  { key: 'subject_facts', label: '(B) Subject Facts', description: 'What facts or details does outlet mention when talking about subject?', category: 'coverage_memory' },
  { key: 'known_for', label: '(C) Known For', description: 'What "known for" descriptors does outlet mention when describing subject?', category: 'coverage_memory' },
];

function parseRawText(raw: Record<string, string | null> | undefined): Record<string, string | null> {
  if (!raw) return {};
  if (raw.raw_text && Object.keys(raw).length === 1) {
    let text = raw.raw_text.trim();
    if (text.startsWith('```')) {
      const nl = text.indexOf('\n');
      if (nl !== -1) text = text.slice(nl + 1);
      if (text.trimEnd().endsWith('```')) text = text.trimEnd().slice(0, -3).trimEnd();
    }
    try {
      const parsed = JSON.parse(text);
      if (typeof parsed === 'object' && parsed !== null) return parsed;
    } catch { /* use as-is */ }
  }
  return raw;
}

function DynamicReflectionFields({
  notes,
  fields,
}: {
  notes: TopicReflectionNotes | SubjectReflectionNotes;
  fields: typeof TOPIC_FIELDS;
}) {
  const notesObj = notes as unknown as Record<string, Record<string, string | null>>;
  return (
    <Stack gap="md">
      {fields.map((f) => {
        const categoryNotes = parseRawText(notesObj[f.category]);
        return (
          <ReflectionFieldCard
            key={f.key}
            label={f.label}
            description={f.description}
            value={categoryNotes[f.key] ?? null}
          />
        );
      })}
    </Stack>
  );
}

function ReflectionItemCard({ item }: { item: ReflectionItem }) {
  const { theme } = useTheme();
  const [expanded, setExpanded] = useState(false);
  const [showArticles, setShowArticles] = useState(false);

  const typeLabel = item.type === 'static' ? 'Static' : item.type === 'dynamic_topic' ? 'Topic' : 'Subject';
  const fm = item.filter_metadata;
  const titles = item.source_article_titles;

  return (
    <div
      style={{
        borderBottom: `1px solid ${theme.colors.border.default}`,
        padding: `${spacing.sm} 0`,
      }}
    >
      <Flex justify="between" align="center">
        <div>
          <span style={{ fontSize: '0.9rem', fontWeight: 500, color: theme.colors.text.primary }}>
            {typeLabel}
          </span>
          {fm && (
            <span style={{ fontSize: '0.75rem', color: theme.colors.text.secondary, marginLeft: spacing.sm }}>
              {fm.articles_before_filter} retrieved &middot; {fm.articles_removed} filtered &middot; {fm.articles_remaining} used
            </span>
          )}
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: theme.colors.primary[500],
            fontSize: '0.85rem',
            fontWeight: 500,
          }}
        >
          {expanded ? 'Collapse' : 'Expand'}
        </button>
      </Flex>

      {expanded && item.consolidated_notes && (
        <div style={{ paddingTop: spacing.sm, paddingLeft: spacing.sm }}>
          <Stack gap="md">
            {item.type === 'static' ? (
              <ReflectionCategoryTabs notes={item.consolidated_notes as StaticReflectionNotes} />
            ) : item.type === 'dynamic_topic' ? (
              <DynamicReflectionFields
                notes={item.consolidated_notes as TopicReflectionNotes}
                fields={TOPIC_FIELDS}
              />
            ) : (
              <DynamicReflectionFields
                notes={item.consolidated_notes as SubjectReflectionNotes}
                fields={SUBJECT_FIELDS}
              />
            )}

            {titles && titles.length > 0 && (
              <div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowArticles(!showArticles)}
                >
                  {showArticles ? 'Hide' : 'Show'} Articles Used ({titles.length})
                </Button>
                {showArticles && (
                  <div
                    style={{
                      marginTop: spacing.xs,
                      maxHeight: titles.length > 10 ? '300px' : undefined,
                      overflowY: titles.length > 10 ? 'auto' : undefined,
                    }}
                  >
                    <Stack gap="xs">
                      {titles.map((a, i) => (
                        <div key={a.uuid || i} style={{ padding: `${spacing.xs} 0`, borderBottom: i < titles.length - 1 ? `1px solid ${theme.colors.border.default}` : undefined }}>
                          <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: 500, color: theme.colors.text.primary }}>{a.title}</p>
                          <span style={{ fontSize: '0.75rem', color: theme.colors.text.secondary }}>{a.date}</span>
                        </div>
                      ))}
                    </Stack>
                  </div>
                )}
              </div>
            )}
          </Stack>
        </div>
      )}
    </div>
  );
}

function PublicationReflectionGroup({ publisher, items }: { publisher: string; items: ReflectionItem[] }) {
  const { theme } = useTheme();
  const [expanded, setExpanded] = useState(false);
  const types = [...new Set(items.map((i) => i.type === 'static' ? 'Static' : i.type === 'dynamic_topic' ? 'Topic' : 'Subject'))];

  return (
    <Card variant="outlined">
      <Card.Header
        title={publisher}
        subtitle={`${items.length} reflection(s): ${types.join(', ')}`}
        action={
          <button
            onClick={() => setExpanded(!expanded)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: theme.colors.primary[500],
              fontSize: '0.85rem',
              fontWeight: 500,
            }}
          >
            {expanded ? 'Collapse' : 'Expand'}
          </button>
        }
      />
      {expanded && (
        <Card.Body>
          {items.map((item, i) => (
            <ReflectionItemCard key={`${item.type}-${item.publication_id}-${i}`} item={item} />
          ))}
        </Card.Body>
      )}
    </Card>
  );
}

export function ExecutionReflectionsView({
  projectId,
  runId,
  executionId,
}: ExecutionReflectionsViewProps) {
  const { theme } = useTheme();
  const [typeFilter, setTypeFilter] = useState('');
  const { downloadExport, loading: exporting } = useExportReflections();
  const { reflections, loading, error } = useExecutionReflections(
    projectId,
    runId,
    executionId,
    typeFilter ? { type: typeFilter } : undefined
  );

  const grouped = useMemo(() => {
    const map = new Map<string, ReflectionItem[]>();
    for (const r of reflections) {
      if (!map.has(r.publisher)) map.set(r.publisher, []);
      map.get(r.publisher)!.push(r);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [reflections]);

  if (loading) {
    return (
      <Card variant="outlined">
        <Card.Body>
          <Flex justify="center" style={{ padding: spacing.lg }}>
            <p style={{ color: theme.colors.text.secondary }}>Loading reflections...</p>
          </Flex>
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <Card variant="outlined">
        <Card.Body>
          <p style={{ color: theme.colors.semantic.error.default }}>
            {error.message || 'Failed to load reflections'}
          </p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Stack gap="md">
      {/* Heading + export */}
      <Flex justify="between" align="center">
        <h2 style={{ fontSize: '1.4rem', fontWeight: 600, color: theme.colors.text.primary, margin: 0 }}>
          Reflection Notes
        </h2>
        <ExportDropdown
          loading={exporting}
          onExport={(format) => downloadExport(projectId, runId, executionId, format)}
        />
      </Flex>

      {/* Filters */}
      <Flex align="end" gap="sm" wrap="wrap">
        <div style={{ minWidth: 160 }}>
          <Select
            label="Filter by Type"
            options={TYPE_OPTIONS}
            value={typeFilter}
            onChange={(value) => setTypeFilter(value)}
          />
        </div>
      </Flex>

      {/* Grouped reflections */}
      {grouped.length === 0 ? (
        <Card variant="outlined">
          <Card.Body>
            <p style={{ color: theme.colors.text.secondary, margin: 0 }}>
              No reflection notes match the selected filters.
            </p>
          </Card.Body>
        </Card>
      ) : (
        grouped.map(([publisher, items]) => (
          <PublicationReflectionGroup key={publisher} publisher={publisher} items={items} />
        ))
      )}
    </Stack>
  );
}
